import { useEffect, useState } from 'preact/hooks';

import { Watermark } from '../containers/containers.jsx';
import * as atm from '../util/atmosphere.js';
import type { CloudCoverGenerator } from '../util/clouds.js';
import type { Scale } from '../util/math.js';
import * as math from '../util/math.js';
import { Parcel } from './parcel.jsx';

export type SkewTProps = {
  width: number;
  height: number;
  yPointer: number | undefined;
  levels: number[];
  temps: number[];
  dewpoints: number[];
  ghs: number[];
  seaLevelPressure: number;
  minPressure: number;
  maxPressure: number;
  minTemp: number;
  maxTemp: number;
  cloudCover: CloudCoverGenerator;
  surfaceElevation: number;
  parcel: atm.ParcelData | undefined;
  formatAltitude: (altitude: number) => number;
  formatTemp: (temp: number) => number;
  tempUnit: string;
  tempAxisStep: number;
  ghUnit: string;
  ghAxisStep: number;
  showUpperClouds: boolean;
};

export function SkewT(props: SkewTProps) {
  const {
    width,
    height,
    levels,
    temps,
    ghs,
    dewpoints,
    minPressure,
    maxPressure,
    seaLevelPressure,
    minTemp,
    maxTemp,
    cloudCover,
    surfaceElevation,
    parcel,
    formatAltitude,
    formatTemp,
    tempUnit,
    ghUnit,
    // Elevation step in windy unit.
    ghAxisStep,
    showUpperClouds,
    yPointer,
  } = props;

  // The full height include the ticks at the bottom.

  const pressureToGhScale = atm.getPressureToGhScale(levels, ghs, seaLevelPressure);
  const ghMeterToPxScale = math.scaleLinear(
    [pressureToGhScale(minPressure), pressureToGhScale(maxPressure)],
    [0, height],
  );
  const ghAxisToPxScale =
    ghUnit === 'm' ? ghMeterToPxScale : math.composeScales(math.scaleLinear([0, 1], [0, 0.3048]), ghMeterToPxScale);
  const pressureToPxScale = math.composeScales(pressureToGhScale, ghMeterToPxScale);

  const tempToPxScale = math.scaleLinear([minTemp, maxTemp], [0, width]);

  const skew = (75 * (width / height) * Math.log10(maxPressure / minPressure)) / (maxTemp - minTemp);

  const pathGenerator = math.svgPath(
    (point: [x: number, y: number]): number => tempToPxScale(point[0]) + skew * (height - pressureToPxScale(point[1])),
    (point: [x: number, y: number]): number => pressureToPxScale(point[1]),
  );

  const ySurface = Math.min(height, Math.round(ghMeterToPxScale(surfaceElevation)));

  let tempAtCursor = 0;
  let dewPointAtCursor = 0;
  let yOffsetCursor = 4;
  let cursorClass = 'top';
  if (yPointer !== undefined) {
    tempAtCursor = math.sampleAt(levels, temps, pressureToPxScale.invert(yPointer));
    dewPointAtCursor = math.sampleAt(levels, dewpoints, pressureToPxScale.invert(yPointer));
    if (yPointer > height / 2) {
      yOffsetCursor = -4;
      cursorClass = 'bottom';
    }
  }

  return (
    <svg width={width} height={height}>
      <g className="graph skewt">
        <rect width={width} height={height} className="background" />
        <Watermark x={Math.round(width / 2)} y={Math.round(height / 2)} />
        <g className="axis">
          <g className="dry-adiabatic">
            {[-20, -10, 0, 10, 20, 30, 40, 50, 60, 70, 80].map((temp) => (
              <DryAdiabatic
                {...{ temp: temp + 273.15, pressure: maxPressure, height, pressureToPxScale, pathGenerator }}
              />
            ))}
          </g>
          <g className="wet-adiabatic">
            {[-20, -10, 0, 5, 10, 15, 20, 25, 30, 35].map((temp) => (
              <WetAdiabatic
                {...{ temp: temp + 273.15, pressure: maxPressure, height, pressureToPxScale, pathGenerator }}
              />
            ))}
          </g>
          <g className="isohume">
            {[-20, -15, -10, -5, 0, 5, 10, 15, 20].map((temp) => (
              <IsoHume
                {...{ temp: temp + 273.15, pressure: maxPressure, height: height, pressureToPxScale, pathGenerator }}
              />
            ))}
          </g>
          <g className="isotherm">
            {[-70, -60, -50, -40, -30, -20, -10, 0, 10, 20, 30, 40].map((temp) => (
              <IsoTherm
                {...{
                  temp: temp + 273.15,
                  height,
                  pressureToPxScale,
                  pathGenerator,
                  tempToPxScale,
                  unit: tempUnit,
                  skew,
                  surfacePx: ySurface,
                  format: formatTemp,
                }}
              />
            ))}
          </g>
          <AltitudeAxis
            {...{
              width,
              ghToPxScale: ghAxisToPxScale,
              step: ghAxisStep,
              unit: ghUnit,
            }}
          />
        </g>
        <g className="cloud">
          <Clouds
            {...{
              width,
              cloudCover,
              pressureToPxScale,
              surfacePressure: pressureToGhScale.invert(surfaceElevation),
              showUpperClouds,
            }}
          />
        </g>
        <g className="line">
          {parcel && <Parcel {...{ parcel, width, height, pathGenerator, pressureToPxScale, formatAltitude }} />}
          <path className="temperature" d={pathGenerator(math.zip(temps, levels))} />
          <path className="dewpoint" d={pathGenerator(math.zip(dewpoints, levels))} />
        </g>
        <rect className="surface" y={ySurface} width={width} height={height - ySurface + 1} />
        {yPointer !== undefined && yPointer < ySurface && (
          <g className={`cursor ${cursorClass}`}>
            <text className="altitude" x={width - 7} y={yPointer + yOffsetCursor}>
              {formatAltitude(ghMeterToPxScale.invert(yPointer))}
            </text>
            <text
              className="temperature"
              x={tempToPxScale(tempAtCursor) + skew * (height - yPointer) + 10}
              y={yPointer + yOffsetCursor}
            >
              {formatTemp(tempAtCursor)}
            </text>
            <text
              className="dewpoint"
              x={tempToPxScale(dewPointAtCursor) + skew * (height - yPointer) + 10}
              y={yPointer + yOffsetCursor}
            >
              {formatTemp(dewPointAtCursor)}
            </text>
            <line y1={yPointer} y2={yPointer} x2={width} />
          </g>
        )}
        <rect width={width} height={height} className="border" />
      </g>

      <g>
        <LayerSwitcher {...{ width, y: 35 }} />
      </g>
    </svg>
  );
}

/**
 * Icons from https://www.svgrepo.com/collection/carbon-design-line-icons/
 */
const LAYERS = [
  {
    name: 'clouds',
    label: 'Clouds',
    paths: [
      'm16 7 1.5.2a8 8 0 0 1 6.4 6.3l.2 1.3 1.4.3a5.5 5.5 0 0 1-1 10.9h-17a5.5 5.5 0 0 1-1-11l1.3-.2.3-1.3A8 8 0 0 1 16 7m0-2a10 10 0 0 0-9.8 8.1A7.5 7.5 0 0 0 7.5 28h17a7.5 7.5 0 0 0 1.3-14.9 10 10 0 0 0-8-8z',
    ],
  },
  {
    name: 'wind',
    label: 'Wind',
    paths: ['M21 15H8v-2h13a3 3 0 1 0-3-3h-2a5 5 0 1 1 5 5m2 13a5 5 0 0 1-5-5h2a3 3 0 1 0 3-3H4v-2h19a5 5 0 0 1 0 10'],
  },
  {
    name: 'rain',
    label: 'Rain',
    paths: [
      'M16 24v-2a3.3 3.3 0 0 0 3-3h2a5.3 5.3 0 0 1-5 5',
      'M16 28a9 9 0 0 1-9-9 10 10 0 0 1 1.5-5l6.7-10.6a1 1 0 0 1 1.6 0L23.5 14a10 10 0 0 1 1.5 5 9 9 0 0 1-9 9m0-22.2-5.8 9.3A8 8 0 0 0 9 19a7 7 0 0 0 14 0 8 8 0 0 0-1.2-4Z',
    ],
  },
  {
    name: 'ccl',
    label: 'Convective Condensation Level (CCL)',
    paths: [
      'm11 18 1.4 1.4 2.6-2.6V29h2V16.8l2.6 2.6L21 18l-5-5z',
      'M23.5 22H23v-2h.5a4.5 4.5 0 0 0 .4-9H23l-.1-.8a7 7 0 0 0-13.9 0v.8h-.9a4.5 4.5 0 0 0 .4 9H9v2h-.5A6.5 6.5 0 0 1 7.2 9.1a9 9 0 0 1 17.6 0A6.5 6.5 0 0 1 23.5 22',
    ],
  },
];

function LayerSwitcher({ width, y }: { width: number; y: number }) {
  const [overlay, setOverlay] = useState(W.store.get('overlay'));

  const padding = 16;
  const compWidth = 32 * LAYERS.length + padding * (LAYERS.length - 1);
  const startX = width / 2 - compWidth / 2;

  useEffect(() => {
    const handleOverlayChange = (overlay: string) => {
      setOverlay(overlay);
    };

    W.store.on('overlay', handleOverlayChange);

    return () => {
      W.store.off('overlay', handleOverlayChange);
    };
  }, []);

  return (
    <g transform={`translate(0, ${y})`}>
      {LAYERS.map(({ name, label, paths }, idx) => (
        <g
          key={name}
          transform={`translate(${startX + idx * (32 + padding)})`}
          className={`layer-icon ${overlay === name ? 'active' : ''}`}
          onPointerDown={() => W.store.set('overlay', name)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              W.store.set('overlay', name);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={label}
          aria-pressed={overlay === name}
        >
          <rect width={32} height={32} />
          {paths.map((d, pathIdx) => (
            <path key={pathIdx} d={d} />
          ))}
        </g>
      ))}
    </g>
  );
}

function DryAdiabatic({
  temp,
  pressure,
  height,
  pressureToPxScale,
  pathGenerator: line,
}: {
  temp: number;
  pressure: number;
  height: number;
  pressureToPxScale: Scale;
  pathGenerator: (points: [x: number, y: number][]) => string;
}) {
  if (height === 0) {
    // prevent an infinite loop when height === 0.
    return;
  }
  const points: [x: number, y: number][] = [];
  const stepPx = height / 20;
  for (let y = height; y > -stepPx; y -= stepPx) {
    const p = pressureToPxScale.invert(y);
    const t = atm.dryLapse(p, temp, pressure);
    points.push([t, p]);
  }

  return <path d={line(points)} />;
}

function WetAdiabatic({
  temp,
  pressure,
  height,
  pressureToPxScale,
  pathGenerator,
}: {
  temp: number;
  pressure: number;
  height: number;
  pressureToPxScale: Scale;
  pathGenerator: (points: [x: number, y: number][]) => string;
}) {
  if (height === 0) {
    // prevent an infinite loop when height === 0.
    return;
  }
  const points: [x: number, y: number][] = [];
  let previousPressure = pressure;
  const stepPx = height / 20;
  for (let y = height; y > -stepPx; y -= stepPx) {
    const p = pressureToPxScale.invert(y);
    temp += (p - previousPressure) * atm.wetTempGradient(p, temp);
    previousPressure = p;
    points.push([temp, p]);
  }

  return <path d={pathGenerator(points)} />;
}

function IsoHume({
  temp,
  pressure,
  height,
  pressureToPxScale,
  pathGenerator,
}: {
  temp: number;
  pressure: number;
  height: number;
  pressureToPxScale: Scale;
  pathGenerator: (points: [x: number, y: number][]) => string;
}) {
  if (height === 0) {
    // prevent an infinite loop when height === 0.
    return;
  }
  const points: [x: number, y: number][] = [];
  const mixingRatio = atm.mixingRatio(atm.saturationVaporPressure(temp), pressure);
  const stepPx = height;
  for (let y = height; y > -stepPx; y -= stepPx) {
    const pressure = pressureToPxScale.invert(y);
    const temp = atm.dewpoint(atm.vaporPressure(pressure, mixingRatio));
    points.push([temp, pressure]);
  }
  return <path d={pathGenerator(points)} />;
}

function IsoTherm({
  temp,
  height,
  pressureToPxScale,
  pathGenerator,
  unit,
  tempToPxScale,
  skew,
  surfacePx,
  format,
}: {
  temp: number;
  height: number;
  pressureToPxScale: Scale;
  pathGenerator: (points: [x: number, y: number][]) => string;
  unit: string;
  tempToPxScale: Scale;
  skew: number;
  surfacePx: number;
  format: (temp: number) => number;
}) {
  const isZero = Math.round(temp) == 273;

  const points: [x: number, y: number][] = [
    [temp, pressureToPxScale.invert(height)],
    [temp, pressureToPxScale.invert(0)],
  ];
  const props = isZero ? { className: 'zero' } : {};

  const x = Math.round(tempToPxScale(temp));
  const angleRad = Math.atan2(-1, skew);
  const angleDeg = Math.round((180 * angleRad) / Math.PI);
  const yText = height - surfacePx + 10;

  return (
    <>
      <path {...props} d={pathGenerator(points)} />
      <text {...props} transform={`translate(${x} ${height}) rotate(${angleDeg}) translate(${yText} -5)`}>{`${format(
        temp,
      )}${isZero ? ` ${unit}` : ''}`}</text>
    </>
  );
}

function AltitudeAxis({
  ghToPxScale,
  width,
  unit,
  step,
}: {
  ghToPxScale: Scale;
  width: number;
  unit: string;
  step: number;
}) {
  const children = [];
  let y = Math.round(ghToPxScale(step));

  for (let elevation = step, isLast; !isLast; elevation += step) {
    const yNext = Math.round(ghToPxScale(elevation + step));
    isLast = yNext < 20;
    children.push(
      <line y1={y} x2={width} y2={y} />,
      <text y={y - 5} x={5}>{`${elevation}${isLast ? ` ${unit}` : ''}`}</text>,
    );
    y = yNext;
  }

  return <g className="altitude">{children}</g>;
}

export type CloudsProp = {
  width: number;
  cloudCover: CloudCoverGenerator;
  pressureToPxScale: Scale;
  surfacePressure: number;
  showUpperClouds: boolean;
};

function Clouds({ width, cloudCover, pressureToPxScale, surfacePressure, showUpperClouds }: CloudsProp) {
  const rects = [];

  let y = 0;

  if (showUpperClouds) {
    y = 30;
    const upperPressure = pressureToPxScale.invert(y);
    const upperCover = cloudCover(upperPressure, 150);

    if (upperCover < 255) {
      rects.push(
        <Cloud y={0} width={width} height={30} cover={upperCover} />,
        <text className="tick" y={30 - 12} x={width - 28} textAnchor="end">
          Upper
        </text>,
        <Cirrus x={width - 20} y={10} scale={0.4}></Cirrus>,
        <line y1="30" y2="30" x2={width} className="boundary" />,
      );
    }
  }

  // Then respect the y scale
  const surfaceY = pressureToPxScale(surfacePressure);
  while (y < surfaceY) {
    const startY = y;
    const cover = cloudCover(pressureToPxScale.invert(y));
    let layerHeight = 1;
    while (y++ < surfaceY && cloudCover(pressureToPxScale.invert(y)) == cover) {
      layerHeight++;
    }
    rects.push(<Cloud y={startY} width={100} height={layerHeight} cover={cover} />);
  }

  return <g>{rects}</g>;
}

function Cloud({ y, height, width, cover }: { y: number; height: number; width: number; cover: number }) {
  if (cover == 255) {
    // We do not want to display a white background for no cloud but nothing.
    // It is more efficient than setting opacity to 0.
    return;
  }
  return <rect {...{ y, height, width }} fill={`rgba(${cover}, ${cover}, ${cover}, 0.7)`} />;
}

function Cirrus({ x, y, scale }: { x: number; y: number; scale: number }) {
  return (
    <g className="cirrus" transform={`translate(${x}, ${y}) scale(${scale})`}>
      <rect x="10" y="10" width="20" height="4" />
      <rect x="0" y="10" width="6" height="4" />
      <rect x="6" y="0" width="20" height="4" />
      <rect x="30" y="0" width="6" height="4" />
      <rect x="6" y="20" width="20" height="4" />
      <rect x="30" y="20" width="6" height="4" />
    </g>
  );
}
